/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Hero section for AI TikTok Video Generator - title, description, CTA, and video showcase
 */
import Link from 'next/link';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const videos = [
  {
    src: 'https://assets.falcocut.ai/tools/tiktok-video-generator/hero-work-bag.mp4',
    poster: 'https://assets.falcocut.ai/tools/tiktok-video-generator/hero-work-bag.webp',
    alt: 'AI generated TikTok video - work bag promotion',
  },
  {
    src: 'https://assets.falcocut.ai/tools/tiktok-video-generator/hero-loungewear.mp4',
    poster:
      'https://assets.falcocut.ai/tools/tiktok-video-generator/hero-loungewear.webp',
    alt: 'AI generated TikTok video - loungewear promotion',
  },
  {
    src: 'https://assets.falcocut.ai/tools/tiktok-video-generator/hero-wellness.mp4',
    poster: 'https://assets.falcocut.ai/tools/tiktok-video-generator/hero-wellness.webp',
    alt: 'AI generated TikTok video - wellness product',
  },
];

// 箭头图标
const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

// 音量图标 - 开启状态
const VolumeOnIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
  </svg>
);

// 音量图标 - 静音状态
const VolumeOffIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
  </svg>
);

const HeroSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % videos.length);
    }, 5000);
  }, []);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    startTimer();
  }, [startTimer]);

  useEffect(() => {
    startTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startTimer]);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      if (index === activeIndex) {
        video.currentTime = 0;
        video.muted = isMuted;
        video.play().catch(() => {});
      } else {
        video.pause();
        video.muted = true;
      }
    });
  }, [activeIndex, isMuted]);

  const handleSelect = (index: number) => {
    setActiveIndex(index);
    resetTimer();
  };

  const toggleMute = () => {
    setIsMuted((prev) => !prev);
  };

  return (
    <section className="w-full bg-[#0A0A0A] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <div className="flex flex-col sm:flex-row items-center gap-10 pad:gap-12 sm:gap-16">
          {/* Left - Text Content */}
          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-3xl pad:text-4xl sm:text-5xl font-bold text-white mb-4 pad:mb-6 leading-tight">
              AI TikTok Video Generator
            </h1>
            <p className="text-base pad:text-lg text-white/72 mb-8 pad:mb-10 leading-relaxed max-w-xl">
              Turn ideas into viral-style TikTok videos — just drop your script, link, or
              clips and let AI handle editing, captions, voice, and music in minutes.
            </p>
            <Link
              href="/ai-video-generator"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
              style={{
                background: 'linear-gradient(90deg, #4D2FFF 0%, #EF5CFF 100%)',
              }}
            >
              <span>Create TikTok Videos with AI</span>
              <ArrowRightIcon />
            </Link>
          </div>

          {/* Right - Video Showcase */}
          <div className="flex-1 w-full max-w-[400px] sm:max-w-none">
            {/* 固定高度容器，避免切换时高度抖动 */}
            <div
              className="flex gap-3 pad:gap-4 justify-center items-stretch"
              style={{ height: 'clamp(300px, 50vw, 480px)' }}
            >
              {videos.map((video, index) => {
                const isActive = index === activeIndex;
                return (
                  <button
                    key={video.src}
                    type="button"
                    onClick={() => handleSelect(index)}
                    className="relative rounded-2xl overflow-hidden transition-[flex] duration-500 ease-in-out h-full"
                    style={{ flex: isActive ? 2.5 : 1 }}
                  >
                    {/* 视频始终存在，避免挂载/卸载 */}
                    <video
                      ref={(el) => {
                        videoRefs.current[index] = el;
                      }}
                      src={video.src}
                      poster={video.poster}
                      muted
                      playsInline
                      loop
                      preload="metadata"
                      className="w-full h-full object-cover"
                    />
                    {/* 非激活项半透明遮罩，替代条件渲染 Image */}
                    <div
                      className="absolute inset-0 bg-black transition-opacity duration-500"
                      style={{ opacity: isActive ? 0 : 0.4 }}
                    />
                  </button>
                );
              })}
            </div>

            {/* Indicators + Volume */}
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className="flex items-center gap-2">
                {videos.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelect(index)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      activeIndex === index ? 'w-8 bg-white' : 'w-2 bg-white/30'
                    }`}
                    aria-label={`Show video ${index + 1}`}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={toggleMute}
                className="ml-3 w-8 h-8 flex items-center justify-center rounded-full bg-[rgba(255,255,255,0.055)]/10 text-white/70 transition-all duration-200 hover:bg-[rgba(255,255,255,0.055)]/20 hover:text-white"
                aria-label={isMuted ? 'Unmute video' : 'Mute video'}
              >
                {isMuted ? <VolumeOffIcon /> : <VolumeOnIcon />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
